import { getAuthClient, getBetterAuthBaseUrl } from "@-/dev.improve.haniverse.lib.auth.client";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";

const SERVER_PREMIUM_QUERY_KEY = "server-premium";

/** How long a premium verdict is trusted before it is refetched. */
const SERVER_PREMIUM_STALE_MS = 5 * 60 * 1000;

/**
 * The server's entitlement verdict for the current session, or `undefined` when there is none.
 *
 * `undefined` covers two cases, both distinct from `false`: the request has not resolved yet, or
 * the viewer is signed out. A signed-out viewer has no server entitlement to ask about — a
 * purchase made without an account lives only in the store receipt on the device (the settings
 * pitch promises "no account needed").
 *
 * No purchase poll: a fresh purchase unlocks through the device status in `useIsPremium`, so
 * nothing waits on Superwall's webhook reaching the server.
 */
export function useServerPremium(): boolean | undefined {
  const { data: session } = getAuthClient().useSession();
  const userId = session?.user?.id;

  const { data } = useQuery({
    // Keyed by user so switching accounts cannot reuse the previous verdict.
    queryKey: [SERVER_PREMIUM_QUERY_KEY, userId ?? null],
    enabled: userId !== undefined,
    staleTime: SERVER_PREMIUM_STALE_MS,
    queryFn: async (): Promise<boolean> => {
      const response = await fetch(`${getBetterAuthBaseUrl()}/api/entitlement/premium`, {
        // Same origin as better-auth, so the session cookie rides along.
        credentials: "include",
      });
      if (!response.ok) {
        return false;
      }
      const body: unknown = await response.json();
      return typeof body === "object" && body !== null && Reflect.get(body, "isPremium") === true;
    },
  });

  if (userId === undefined) {
    return undefined;
  }
  return data;
}
